import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoginCredentials } from '@/shared/types';
import { AdminUser } from '../api/admin-api';
import { sessionService } from '@/shared/services/session.service';

// Tipos de acciones para las sagas
export interface AdminLoginRequest {
  credentials: LoginCredentials;
}

export interface AdminRefreshTokenRequest {
  refreshToken: string;
}

export interface AdminChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// Estado inicial
export interface AdminState {
  user: AdminUser | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  permissions: string[];
  role: 'SUPER_ADMIN' | 'AGENCY_ADMIN' | null;
}

const initialState: AdminState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  loading: false, // Cambiado a false para evitar que aparezca cargando al inicio
  error: null,
  permissions: [],
  role: null,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // Acciones que disparan sagas
    adminLoginRequest: (state, _action: PayloadAction<AdminLoginRequest>) => {
      state.loading = true;
      state.error = null;
    },
    adminLogoutRequest: state => {
      state.loading = true;
      state.error = null;
    },
    adminRefreshTokenRequest: (
      state,
      _action: PayloadAction<AdminRefreshTokenRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    adminVerifyTokenRequest: state => {
      state.loading = true;
      state.error = null;
    },
    adminChangePasswordRequest: (
      state,
      _action: PayloadAction<AdminChangePasswordRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },

    // Acciones que actualizan el estado (llamadas desde sagas)
    adminLoginSuccess: (
      state,
      action: PayloadAction<{
        user: AdminUser;
        token: string;
        refreshToken: string;
        permissions: string[];
        role: 'SUPER_ADMIN' | 'AGENCY_ADMIN';
      }>
    ) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.permissions = action.payload.permissions;
      state.role = action.payload.role;
      state.isAuthenticated = true;
      state.error = null;

      // Clear any previous regular user session to avoid role conflicts
      sessionService.clearSession(false); // ← clear regular session

      // Guardar sesión usando SessionService con flag isAdmin=true
      sessionService.saveSession(
        {
          user: action.payload.user,
          token: action.payload.token,
          refreshToken: action.payload.refreshToken,
        },
        true
      ); // ← isAdmin = true
    },
    adminLogoutSuccess: state => {
      state.loading = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.permissions = [];
      state.role = null;
      state.isAuthenticated = false;
      state.error = null;

      // Limpiar sesión usando SessionService con flag isAdmin=true
      sessionService.clearSession(true); // ← isAdmin = true
    },
    adminRefreshTokenSuccess: (
      state,
      action: PayloadAction<{ token: string; refreshToken: string }>
    ) => {
      state.loading = false;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.error = null;
    },
    adminVerifyTokenSuccess: (state, action: PayloadAction<AdminUser>) => {
      state.loading = false;
      state.user = action.payload;
      state.permissions = action.payload.permissions || [];
      state.role = action.payload.role as 'SUPER_ADMIN' | 'AGENCY_ADMIN' | null;
      state.isAuthenticated = true;
      state.error = null;
    },
    adminChangePasswordSuccess: state => {
      state.loading = false;
      state.error = null;
    },
    adminFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    adminClearError: state => {
      state.error = null;
    },
    adminClearAuth: state => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.permissions = [];
      state.role = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;

      // Limpiar sesión usando SessionService con flag isAdmin=true
      sessionService.clearSession(true); // ← isAdmin = true
    },
    adminInitializeAuth: state => {
      try {
        const session = sessionService.restoreSession(true); // ← isAdmin = true

        if (session && session.user) {

          state.user = session.user as AdminUser;
          state.token = session.token || null;
          state.refreshToken = session.refreshToken || null;
          state.permissions = (session.user as AdminUser).permissions || [];
          state.role = (session.user as AdminUser).role as
            | 'SUPER_ADMIN'
            | 'AGENCY_ADMIN'
            | null;
          state.isAuthenticated = true;
          state.error = null;
          state.loading = false;
        } else {
          // Si no hay sesión válida, limpiar estado
          state.user = null;
          state.token = null;
          state.refreshToken = null;
          state.permissions = [];
          state.role = null;
          state.isAuthenticated = false;
          state.error = null;
          state.loading = false;
        }
      } catch (error) {
        sessionService.clearSession(true); // ← isAdmin = true
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.permissions = [];
        state.role = null;
        state.isAuthenticated = false;
        state.error = null;
        state.loading = false;
      }
    },
  },
});

export const {
  adminLoginRequest,
  adminLogoutRequest,
  adminRefreshTokenRequest,
  adminVerifyTokenRequest,
  adminChangePasswordRequest,
  adminLoginSuccess,
  adminLogoutSuccess,
  adminRefreshTokenSuccess,
  adminVerifyTokenSuccess,
  adminChangePasswordSuccess,
  adminFailure,
  adminClearError,
  adminClearAuth,
  adminInitializeAuth,
} = adminSlice.actions;

export default adminSlice.reducer;
